import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useTranslation } from '../context/TranslationContext';
import Button from '../ui/Button';
import Icon from '../ui/Icon';

import styles from './PrivacyPolicy.module.css';

const LANGUAGES = ['tr', 'en'];

// Each section renders `privacy.<id>.title`, then `p1..pN`, then a list of
// `item1..itemN`, then `note` when `note` is set. Copy lives in TranslationContext.
const SECTIONS = [
  { id: 'operator', paragraphs: 1 },
  { id: 'collected', paragraphs: 1, items: 7 },
  { id: 'ai', paragraphs: 2, items: 3, note: true },
  { id: 'storage', paragraphs: 2 },
  { id: 'signin', paragraphs: 1 },
  { id: 'tracking', paragraphs: 1 },
  { id: 'family', paragraphs: 1 },
  { id: 'sharing', paragraphs: 1 },
  { id: 'deletion', paragraphs: 2, items: 5, note: true },
  { id: 'rights', paragraphs: 1 },
  { id: 'children', paragraphs: 1 },
  { id: 'changes', paragraphs: 1 },
];

const range = (n) => Array.from({ length: n || 0 }, (_, i) => i + 1);

function PolicySection({ id, paragraphs, items, note }) {
  const { t } = useTranslation();
  const key = (suffix) => `privacy.${id}.${suffix}`;

  return (
    <section className={styles.section} aria-labelledby={`privacy-${id}`}>
      <h2 id={`privacy-${id}`} className={styles.sectionTitle}>{t(key('title'))}</h2>
      {range(paragraphs).map((n) => (
        <p key={n} className={styles.paragraph}>{t(key(`p${n}`))}</p>
      ))}
      {items > 0 && (
        <ul className={styles.list}>
          {range(items).map((n) => (
            <li key={n}>{t(key(`item${n}`))}</li>
          ))}
        </ul>
      )}
      {note && <p className={styles.paragraph}>{t(key('note'))}</p>}
    </section>
  );
}

export default function PrivacyPolicy() {
  const { t, language, setLanguage } = useTranslation();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('lang');
  const email = t('privacy.contactEmail');

  // `/privacy?lang=en` lets store listings link straight to one language.
  useEffect(() => {
    if (LANGUAGES.includes(requested)) setLanguage(requested);
  }, [requested, setLanguage]);

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <Link to="/" className={styles.brand}>
          <span className={styles.brandIcon}>
            <Icon name="fa-scale-balanced" size="lg" strokeWidth={2} />
          </span>
          <span className={styles.brandText}>{t('appName')}</span>
        </Link>
        <div className={styles.langToggle} role="group" aria-label={t('privacy.language')}>
          {LANGUAGES.map((lang) => (
            <Button
              key={lang}
              variant={language === lang ? 'secondary' : 'ghost'}
              size="sm"
              aria-pressed={language === lang}
              onClick={() => setLanguage(lang)}
            >
              {lang.toUpperCase()}
            </Button>
          ))}
        </div>
      </header>

      <main className={styles.content}>
        <h1 className={styles.title}>{t('privacy.title')}</h1>
        <p className={styles.updated}>{t('privacy.lastUpdated')}</p>
        <p className={styles.lead}>{t('privacy.intro')}</p>

        {SECTIONS.map((section) => (
          <PolicySection key={section.id} {...section} />
        ))}

        <section className={styles.section} aria-labelledby="privacy-contact">
          <h2 id="privacy-contact" className={styles.sectionTitle}>{t('privacy.contact.title')}</h2>
          <p className={styles.paragraph}>
            {t('privacy.contact.p1')}{' '}
            <a href={`mailto:${email}`}>{email}</a>
          </p>
        </section>

        <p className={styles.back}>
          <Link to="/">{t('privacy.backHome')}</Link>
        </p>
      </main>
    </div>
  );
}
